import { Check } from "lucide-react"
import { useBooking } from "@/context/BookingContext"
import {
  formatMoney,
  getGrandTotal,
  getPackagePricePerPerson,
  getSelectedAddOns,
  getSelectedStayPeriod,
  getNightCount,
} from "@/lib/booking"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { lookupCityCode } from "@/lib/airports"

const HotelSvg = ({ color = "var(--primary)" }: { color?: string }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path
      d="M4.66667 8.66667C5.77333 8.66667 6.66667 7.77333 6.66667 6.66667C6.66667 5.56 5.77333 4.66667 4.66667 4.66667C3.56 4.66667 2.66667 5.56 2.66667 6.66667C2.66667 7.77333 3.56 8.66667 4.66667 8.66667ZM12.6667 4.66667H8.66667C7.93333 4.66667 7.33333 5.26667 7.33333 6V9.33333H2V4C2 3.63333 1.7 3.33333 1.33333 3.33333C0.966667 3.33333 0.666667 3.63333 0.666667 4V12.6667C0.666667 13.0333 0.966667 13.3333 1.33333 13.3333C1.7 13.3333 2 13.0333 2 12.6667V11.3333H14V12.6667C14 13.0333 14.3 13.3333 14.6667 13.3333C15.0333 13.3333 15.3333 13.0333 15.3333 12.6667V7.33333C15.3333 5.86 14.14 4.66667 12.6667 4.66667Z"
      fill={color}
    />
  </svg>
)

const PlaneSvg = ({ color = "var(--primary)" }: { color?: string }) => (
  <svg width="14" height="14" viewBox="0 0 13.3333 13.0994" fill="none">
    <path
      d="M5.33333 7.86667L1.11667 9.55C0.85 9.66111 0.597333 9.63622 0.358667 9.47533C0.12 9.31444 0.000444444 9.08933 0 8.8V8.43333C0 8.3 0.0306667 8.17222 0.092 8.05C0.153333 7.92778 0.239333 7.82778 0.35 7.75L5.33333 4.26667V1.33333C5.33333 0.966668 5.464 0.65289 5.72533 0.392001C5.98667 0.131112 6.30044 0.000445574 6.66667 1.12994e-06C7.03289 -0.000443314 7.34689 0.130223 7.60867 0.392001C7.87044 0.653779 8.00089 0.967557 8 1.33333V4.26667L12.9833 7.75C13.0944 7.82778 13.1807 7.92778 13.242 8.05C13.3033 8.17222 13.3338 8.3 13.3333 8.43333V8.8C13.3333 9.08889 13.214 9.314 12.9753 9.47533C12.7367 9.63667 12.4838 9.66156 12.2167 9.55L8 7.86667V10.2667L9.71667 11.4667C9.80556 11.5333 9.87511 11.614 9.92533 11.7087C9.97556 11.8033 10.0004 11.906 10 12.0167V12.4167C10 12.6389 9.90822 12.8196 9.72467 12.9587C9.54111 13.0978 9.33844 13.1338 9.11667 13.0667L6.66667 12.3333L4.21667 13.0667C3.99444 13.1333 3.79156 13.0973 3.608 12.9587C3.42444 12.82 3.33289 12.6393 3.33333 12.4167V12.0167C3.33333 11.9056 3.35844 11.8029 3.40867 11.7087C3.45889 11.6144 3.52822 11.5338 3.61667 11.4667L5.33333 10.2667V7.86667Z"
      fill={color}
    />
  </svg>
)

export function PackageSummary() {
  const { state } = useBooking()
  const selectedStayPeriod = getSelectedStayPeriod(state)
  const selectedAddOns = getSelectedAddOns(state)
  const packagePricePerPerson = getPackagePricePerPerson(state)
  const grandTotal = getGrandTotal(state)
  const nightCount = getNightCount(
    selectedStayPeriod?.stayStartDate,
    selectedStayPeriod?.stayEndDate
  )
  const selectedFlightDestination = state.selectedFlight
    ? (lookupCityCode(state.selectedFlight.outbound.destinationAirportCode)?.city ??
      state.destinationAirport?.city ??
      (state.event?.airport ? lookupCityCode(state.event.airport)?.city : undefined) ??
      state.event?.airport ??
      state.selectedFlight.outbound.destinationAirportCode)
    : null

  if (!state.selectedHotel && state.packageType !== "flight-only") return null
  if (state.packageType === "flight-only" && !state.selectedFlight) return null

  return (
    <Card className="shadow-lg">
      <CardHeader className="border-b">
        <CardTitle className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
          Your Package
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {state.selectedHotel && (
          <div className="flex items-start gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <HotelSvg />
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Hotel</span>
              <span className="text-sm font-semibold text-foreground">
                {state.selectedHotel.name}
              </span>
              <span className="text-xs text-muted-foreground">
                {state.selectedHotel.distanceFromVenue || "Venue distance on request"}
              </span>
            </div>
          </div>
        )}

        {(state.packageType === "flight-hotel" || state.packageType === "flight-only") && state.selectedFlight && (
          <div className="flex items-start gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <PlaneSvg />
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Flight</span>
              <span className="text-sm font-semibold text-foreground">
                {state.selectedFlight.departureAirport.city} –{" "}
                {selectedFlightDestination}
              </span>
              <span className="text-xs text-muted-foreground">
                {state.selectedFlight.outbound.travelClass} · {nightCount} nights
              </span>
            </div>
          </div>
        )}
      </CardContent>

      <CardContent className="flex flex-col gap-4 border-t pt-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Package / person</span>
          <span className="text-lg font-bold text-foreground">
            {formatMoney(packagePricePerPerson, state.currency)}
          </span>
        </div>

        {selectedAddOns.length > 0 && (
          <>
            <span className="text-xs font-semibold tracking-widest text-primary uppercase">
              ★ Extras
            </span>
            {selectedAddOns.map(({ addOn, quantity }) => (
              <div key={addOn.id} className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Check className="size-3.5 text-primary" />
                  {addOn.note ? `${addOn.name} - ${addOn.note}` : addOn.name} ×{quantity}
                </span>
                <span className="text-sm text-foreground">
                  +{formatMoney(addOn.priceDeltaPerPerson * quantity, state.currency)}
                </span>
              </div>
            ))}
          </>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Total ({state.passengerCount}{" "}
            {state.passengerCount === 1 ? "traveler" : "travelers"})
          </span>
          <span className="text-2xl font-bold text-primary">
            {formatMoney(grandTotal, state.currency)}
          </span>
        </div>
      </CardContent>

      <CardFooter className="justify-center">
        <p className="text-xs text-muted-foreground">
          Live pricing · Secure payment
        </p>
      </CardFooter>
    </Card>
  )
}
